<template>
  <div id="app">
    <nav class="navbar">
      <div class="nav-container">
        <router-link
          to="/"
          class="nav-brand"
          :title="$t('nav.brandTitle')"
          @click="handleBrandClick"
        >
          🎮 steamplayti.me
        </router-link>
        <div class="nav-links">
          <router-link
            to="/blog"
            class="nav-link"
            :title="$t('nav.blogTitle')"
          >
            {{ $t('nav.blog') }}
          </router-link>
          <!-- Both go to /settings: signed out, that page explains what a
               login is for and holds the Steam button. Never a silent
               redirect to Steam from the nav. -->
          <router-link
            to="/settings"
            class="nav-link"
            :title="signedIn ? '' : $t('nav.signInTitle')"
          >
            {{ signedIn ? $t('nav.settings') : $t('nav.signIn') }}
          </router-link>
          <div v-if="showLangSwitch" class="lang-switch" role="group" aria-label="Language">
            <button
              v-for="lang in languages"
              :key="lang"
              type="button"
              class="lang-btn"
              :class="{ 'lang-btn--active': currentLocale === lang }"
              @click="switchLanguage(lang)"
            >{{ lang.toUpperCase() }}</button>
          </div>
        </div>
      </div>
    </nav>

    <main class="main-content">
      <router-view />
    </main>

    <footer class="site-footer">
      <nav class="footer-tools" :aria-label="$t('footer.toolsLabel')">
        <span class="footer-tools-label">{{ $t('footer.toolsLabel') }}</span>
        <router-link
          v-for="page in toolPages"
          :key="page.slug"
          class="footer-tool-link"
          :to="page.path"
        >{{ page.shortTitle }}</router-link>
      </nav>
      <div class="footer-container">
        <span>
          {{ $t('footer.builtBy') }} <a href="http://steamcommunity.com/profiles/76561198208735799" target="_blank" rel="noopener noreferrer">katates</a>
        </span>
        <span class="dot">•</span>
        <span>
          <a href="https://store.steampowered.com/wishlist/id/katates/?sort=price" target="_blank" rel="noopener noreferrer">{{ $t('footer.gift') }}</a>
        </span>
        <span class="dot">•</span>
        <span>
          <a href="https://www.patreon.com/cw/katates?utm_medium=unknown&utm_source=join_link&utm_campaign=creatorshare_creator&utm_content=copyLink" target="_blank" rel="noopener noreferrer">{{ $t('footer.donate') }}</a>
        </span>
        <span class="dot">•</span>
        <span>
          <a href="mailto:hello@steamplayti.me">{{ $t('footer.help') }}</a>
        </span>
        <span class="dot">•</span>
        <span>
          <router-link to="/about/">{{ $t('footer.about') }}</router-link>
        </span>
        <span class="dot">•</span>
        <span>
          <router-link to="/privacy/">{{ $t('footer.privacy') }}</router-link>
        </span>
      </div>
    </footer>
  </div>
</template>

<script>
import { setLocale, SUPPORTED_LOCALES } from './i18n'
import { SEO_PAGES } from './utils/seoPages'
import { accountAPI } from './services/api'

export default {
  name: 'App',
  data() {
    return {
      userProfile: { display_name: '', avatar_url: '', profile_url: '', isSynced: false },
      steamId: '',
      viewVersion: 0,
      languages: SUPPORTED_LOCALES,
      toolPages: SEO_PAGES,
      signedIn: accountAPI.probablySignedIn(),
    }
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale
    },
    showLangSwitch() {
      // The blog, /page/* SEO views and the about/privacy pages are
      // English-only by design, so the TR/EN switch would be a dead control there.
      const path = this.$route?.path || ''
      return !['/blog', '/page', '/about', '/privacy'].some((prefix) => path.startsWith(prefix))
    },
    hasProfile() {
      return !!(this.userProfile.display_name || this.userProfile.avatar_url)
    },
    isHome() {
      
      try { return this.$route?.name === 'DashboardHome' && !this.steamId } catch (_) { return true }
    },
    showNavUser() {
      
      try {
        const routeName = this.$route?.name
        return this.hasProfile && (routeName === 'Dashboard' || routeName === 'DashboardHome')
      } catch (_) {
        return this.hasProfile
      }
    },
    steamProfileLink() {
      if (this.userProfile?.profile_url) {
        return this.userProfile.profile_url
      }
      if (this.steamId) {
        return `https://steamcommunity.com/profiles/${this.steamId}`
      }
      return ''
    },
  },
  watch: {
    // The login cookie can appear mid-session (a claim's Steam return opens it
    // too), so re-read the marker on every navigation. Cheap: no request.
    '$route.fullPath'() {
      this.signedIn = accountAPI.probablySignedIn()
    },
    '$route.params.steamId'(newId) {
      if (!newId) {
        this.steamId = ''
      }
    }
  },
  mounted() {
    
    this._profileHandler = (e) => {
      const detail = e.detail || {}
      const incoming = detail.profile || {}
      this.userProfile = {
        ...incoming,
        display_name: incoming.display_name || '',
        avatar_url: incoming.avatar_url || '',
        profile_url: incoming.profile_url || '',
        isSynced: typeof incoming.isSynced === 'boolean'
          ? incoming.isSynced
          : (typeof incoming.is_synced === 'boolean' ? incoming.is_synced : false),
      }
      this.steamId = detail.steamId || ''
    }
    window.addEventListener('steamProfileUpdated', this._profileHandler)
  },
  beforeUnmount() {
    window.removeEventListener('steamProfileUpdated', this._profileHandler)
  },
  methods: {
    switchLanguage(lang) {
      setLocale(lang)
    },
    handleBrandClick(event) {
      if (this.steamId) {
        if (event && typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        this.exitProfile()
      }
    },
    exitProfile() {
      this.userProfile = { display_name: '', avatar_url: '', profile_url: '', isSynced: false }
      this.steamId = ''
      try { window.dispatchEvent(new CustomEvent('steamProfileUpdated', { detail: { profile: this.userProfile, steamId: '' } })) } catch (_) {}
      
      this.viewVersion++
      try { this.$router.replace({ name: 'DashboardHome' }) } catch (_) { try { window.location.assign('/') } catch (_) {} }
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --bg-1: #0b0e12;
  --bg-2: #171a21;
  --panel: #1b2838;
  --panel-border: #2a475e;
  --text: #c7d5e0;
  --muted: #8f98a0;
  --primary: #66c0f4;
  --primary-hover: #8ad1fa;
  --danger: #d9534f;
  --success: #5cb85c;
}

body {
  font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
  background: linear-gradient(135deg, var(--bg-1) 0%, var(--bg-2) 100%);
  min-height: 100vh;
  color: var(--text);
}

#app { min-height: 100vh; display: flex; flex-direction: column; }

.navbar {
  background: #171a21;
  border-bottom: 1px solid var(--panel-border);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.nav-container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
}
@media (max-width: 768px) {
  .nav-container { flex-direction: column; gap: 8px; height: auto; padding: 8px 16px; }
  /* index.html's critical CSS makes .nav-brand inline-flex, so text-align
     can't center it — the flex content needs justify-content instead. */
  .nav-brand { font-size: 1.1rem; text-align: center; width: 100%; justify-content: center; }
}

.nav-user {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(23, 26, 33, 0.85);
  border: 1px solid rgba(102, 192, 244, 0.18);
  border-radius: 16px;
  padding: 14px 18px;
  margin: 0 auto 28px;
  width: 100%;
  box-shadow: 0 22px 45px rgba(8, 12, 24, 0.32);
}

.nav-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(102, 192, 244, 0.35);
}

.nav-username {
  color: var(--text);
  font-weight: 600;
  flex: 1;
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

a.nav-username {
  text-decoration: none;
  transition: color 0.2s ease;
}

a.nav-username:hover {
  color: var(--primary);
}

.nav-exit {
  background: transparent;
  border: none;
  color: var(--muted);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 6px;
  transition: color 0.2s ease;
}
.nav-exit:hover {
  color: var(--primary);
}

@media (max-width: 768px) {
  .nav-user {
    margin: 16px auto 20px;
    padding: 12px 14px;
    gap: 10px;
    max-width: 100%;
  }

  .nav-avatar {
    width: 48px;
    height: 48px;
  }
}

.nav-brand {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text);
  text-decoration: none;
  transition: color 0.2s ease;
}

.nav-brand:hover {
  color: var(--primary);
}

.nav-links {
  display: flex;
  gap: 30px;
  align-items: center;
}

.lang-switch {
  display: flex;
  gap: 4px;
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  padding: 3px;
}

.lang-btn {
  background: transparent;
  border: none;
  color: var(--muted);
  font-size: 0.78rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;
}

.lang-btn:hover {
  color: var(--text);
}

.lang-btn--active {
  background: rgba(102, 192, 244, 0.15);
  color: var(--primary);
}

.nav-link {
  color: var(--muted);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  position: relative;
}

.nav-link:hover,
.nav-link.router-link-active {
  color: var(--primary);
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--primary);
  transition: width 0.3s ease;
}

.nav-link:hover::after,
.nav-link.router-link-active::after {
  width: 100%;
}

/* `flex: 1 1 auto` (allow shrink) + `min-width: 0` so a child with a
   wide intrinsic min-content (long unwrappable label, table row) cannot
   force <main> wider than the viewport. Without these two together the
   default `min-width: auto` on flex items pegs <main> to its content's
   min-content size, which on narrow viewports causes horizontal scroll. */
.main-content { flex: 1 1 auto; min-width: 0; }
.site-footer { margin-top: auto; background: #171a21; border-top: 1px solid var(--panel-border); }
.footer-tools { max-width: 1440px; margin: 0 auto; padding: 16px 20px 0; display: flex; gap: 8px 16px; flex-wrap: wrap; align-items: center; justify-content: center; }
.footer-tools-label { color: var(--muted); font-size: 0.8rem; letter-spacing: 0.5px; text-transform: uppercase; font-weight: 600; }
.footer-tool-link { color: var(--muted); text-decoration: none; font-size: 0.9rem; }
.footer-tool-link:hover { color: var(--primary); }
.footer-container { max-width: 1440px; margin: 0 auto; padding: 16px 20px; display: flex; gap: 10px; flex-wrap: wrap; align-items: center; justify-content: center; color: var(--muted) }
.footer-container a { color: var(--primary); text-decoration: none }
.footer-container .dot { opacity: 0.6 }

.main-content {
  max-width: 1440px;
  margin: 0 auto;
  padding: 40px 20px;
}
/* Global alerts for consistent error/success messaging */
.alert {
  background: var(--panel);
  color: var(--text);
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid var(--panel-border);
  border-left: 4px solid transparent;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  margin: 12px 0 8px;
}
.alert:last-child { margin-bottom: 0; }
.alert-danger { border-left-color: var(--danger); }
.alert-success { border-left-color: var(--success); }
.alert-ico { line-height: 1; margin-top: 2px; font-size: 1.1rem; flex: 0 0 auto; }
.alert-success .alert-ico { color: var(--success); }
.alert-danger .alert-ico { color: var(--danger); }
.alert-text { flex: 1; line-height: 1.4; }

.site-footer {
  margin-top: 40px;
  border-top: 1px solid var(--panel-border);
  background: #12151a;
}

.footer-container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 16px 20px;
  color: var(--muted);
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
}

.footer-container a { color: var(--primary); text-decoration: none; }
.footer-container a:hover { color: var(--primary-hover); }
.footer-container .dot { opacity: 0.6; }

@media (max-width: 768px) {
  .navbar {
    position: static;
    top: auto;
  }
  .nav-container {
    flex-direction: column;
    height: auto;
    padding: 20px;
  }
  
  .nav-links {
    margin-top: 15px;
    gap: 20px;
  }

  .nav-user {
    margin-top: 10px;
    align-self: stretch;
    justify-content: center;
  }
  
  .main-content {
    padding: 20px;
  }
}
</style> 
